import { SlateUtils } from '@all-hands-hq/plex-graphql-schema';
import { Button, ButtonGroup, createStyles, makeStyles, Theme } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import assertNever from 'assert-never';
import React, { useEffect, useState } from 'react';

import {
  PostDetailsFragment,
  PostType,
  PublishStatus,
  useGetPostQuery,
  useUpdateEventMutation,
  useUpdatePostMutation,
} from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { PlexHeader } from '../../details/plex/PlexHeader';
import { logAnalytics } from '../../shared/logAnalytics';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { ConcurrentUpdateSnackbar } from '../ConcurrentUpdateSnackbar';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { useDialogState } from '../useDialogState';
import { PostDialogContent, PostFormData, postFormDataToInput } from './PostDialogContent';

type Props = {
  postId: string;
};

type State = PostFormData | null;

const initializeState = (
  setState: React.Dispatch<React.SetStateAction<State>>,
  post: PostDetailsFragment,
  options?: { initializeOnlyIfEmpty: boolean }
): void => {
  const updatedState = {
    title: post.title,
    body: SlateEditorValue.from(post.body),
    entities: { expanded_link: post.links, mention: post.mentions },
    primaryLinkId: post.primaryLink?.id,
    labelIds: post.labels.map((label) => label.id),
  };

  switch (post.type) {
    case PostType.Post: {
      setState((initialState) =>
        initialState == null || !options?.initializeOnlyIfEmpty
          ? { ...updatedState, type: PostType.Post, hasConcurrentUpdate: false }
          : { ...initialState, hasConcurrentUpdate: true }
      );
      break;
    }
    case PostType.Event: {
      setState((initialState) =>
        initialState == null || !options?.initializeOnlyIfEmpty
          ? { ...updatedState, type: PostType.Event, eventDate: post.eventDate, hasConcurrentUpdate: false }
          : { ...initialState, hasConcurrentUpdate: true }
      );
      break;
    }
    case PostType.Milestone:
    case PostType.Task: {
      throw new Error(`Cannot edit ${post.type} via UpdatePost`);
    }
    default: {
      assertNever(post.type);
    }
  }
};

export function UpdatePostDialog({ postId }: Props): React.ReactElement {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const [state, setState] = useState<State>(null);
  // TODO: Handle errors
  const { data, loading } = useGetPostQuery({ variables: { postId } });
  const [updatePostMutation, { loading: updatePostLoading }] = useUpdatePostMutation();
  const [updateEventMutation, { loading: updateEventLoading }] = useUpdateEventMutation();
  const updateLoading = updatePostLoading || updateEventLoading;
  const fileUploading = state?.body != null && SlateUtils.hasUploadsInProgress(state.body.value);
  const post: PostDetailsFragment | null | undefined = data?.loggedIn?.postById;

  useEffect(() => {
    if (!loading && post != null) {
      initializeState(setState, post, { initializeOnlyIfEmpty: true });
    }
  }, [postId, loading, post]);

  const handleClose = (): void => {
    setDialogState(null);
  };

  const handleSave = (publishStatus: PublishStatus): void => {
    if (state != null) {
      const update = (): Promise<unknown> => {
        switch (state.type) {
          case PostType.Post: {
            return updatePostMutation({
              variables: {
                post: {
                  ...postFormDataToInput(state),
                  id: postId,
                  publishStatus,
                },
              },
            });
          }
          case PostType.Event: {
            return updateEventMutation({
              variables: {
                event: {
                  ...postFormDataToInput(state),
                  id: postId,
                  publishStatus,
                  eventDate: state.eventDate,
                },
              },
            });
          }
        }
      };

      update().then(() => {
        logAnalytics({ name: 'post updated', details: { plexId: data?.loggedIn?.postById?.plex?.id, itemId: postId } });
        handleClose();
      });
    }
  };

  const handleUpdate = (): void => {
    handleSave(post?.publishStatus || PublishStatus.Internal);
  };

  const handleUpdateDraft = (): void => {
    handleSave(PublishStatus.Draft);
  };

  const handlePostInternally = (): void => {
    handleSave(PublishStatus.Internal);
  };

  const handlePostFormChange = (postFormData: PostFormData): void => {
    setState(postFormData);
  };

  return (
    <div className={classes.root}>
      <DialogTopBar>
        {!!data?.loggedIn?.postById?.plex && (
          <PlexHeader
            className={classes.topbar}
            plexName={data.loggedIn.postById.plex.name}
            plexColor={data.loggedIn.postById.plex.color}
            plexPublishStatus={data.loggedIn.postById.publishStatus}
            publishedDate={data.loggedIn.postById.publishedDate}
            authorName={data.loggedIn.postById.author.name}
            authorAvatar={data.loggedIn.postById.author.picture}
            authorTitle={data.loggedIn?.postById.author.jobTitle}
            showUnreadBadge={false}
          />
        )}
      </DialogTopBar>

      <div>{loading && <Skeleton variant="text" />}</div>

      {state != null && (
        <PostDialogContent
          post={state}
          onPostChange={handlePostFormChange}
          dialogTitle={state.type === PostType.Post ? 'Update Post' : 'Update Event'}
        />
      )}

      <DialogActions
        cta={
          post?.publishStatus === PublishStatus.Draft ? (
            <div>
              <ButtonGroup disabled={loading || fileUploading || updateLoading}>
                <Button onClick={handleUpdateDraft} variant="contained" color="primary">
                  Update Draft
                </Button>
                <MoreOptionsMenu
                  id={'new'}
                  variant={'contained'}
                  color={'primary'}
                  size={'small'}
                  options={[
                    {
                      label: 'Post internally',
                      onClick: handlePostInternally,
                    },
                  ]}
                  Button={Button}
                  menuProps={{
                    marginThreshold: 0,
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    transformOrigin: { horizontal: 'right', vertical: 'bottom' },
                  }}
                />
              </ButtonGroup>
            </div>
          ) : (
            <div>
              <Button
                onClick={handleUpdate}
                variant={'contained'}
                color={'primary'}
                disabled={loading || fileUploading || updateLoading}
              >
                Update
              </Button>
            </div>
          )
        }
      />

      <ConcurrentUpdateSnackbar
        open={state?.hasConcurrentUpdate && !updateLoading}
        onRefresh={(): void => {
          if (post != null) {
            initializeState(setState, post);
          }
        }}
        onDismiss={(): void => {
          setState((initialState) =>
            initialState != null ? { ...initialState, hasConcurrentUpdate: false } : initialState
          );
        }}
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    topbar: {
      paddingRight: 0,
    },
    rootTitle: {
      fontWeight: 'bold',
      marginLeft: spacings.xxlarge,
      marginTop: spacings.xlarge,
      marginBottom: spacings.xlarge,
    },
    rootTitleIcon: {
      width: 32,
      height: 32,
      marginRight: spacings.normal,
    },
  })
);
